import type { NodeTypeConfig } from '../config/node-types';
import type { CardDiscoveryGroup, CardDiscoveryRole } from '../card-engine/types';

export type FlowCatalogRole = 'when' | 'and' | 'do';
export type FlowCatalogBrowseMode = 'all' | 'rooms' | 'criteria';

export interface FlowCatalogAvailability {
	available: boolean;
	label?: string;
}

export interface FlowCatalogDevice {
	id: string;
	name: string;
	roomId: string;
	roomName: string;
	channel: string;
	icon: string;
	device: any;
	actionComponents?: any[];
	conditionComponents?: any[];
}

export interface FlowCatalogCard {
	id: string;
	nodeType: string;
	paletteKey: string;
	title: string;
	sentence: string;
	identity: string;
	icon: string;
	color: string;
	role: FlowCatalogRole;
	group: CardDiscoveryGroup;
	groupLabel: string;
	breadcrumbs: string[];
	searchText: string;
	highlighted: boolean;
	premiumOnly: boolean;
	availability: FlowCatalogAvailability;
	initialData?: Record<string, unknown>;
}

export interface FlowCatalogRoom {
	id: string;
	name: string;
	devices: FlowCatalogDevice[];
}

export interface FlowCatalogModel {
	cards: FlowCatalogCard[];
	criteria: Array<{ id: CardDiscoveryGroup; label: string; count: number }>;
	rooms: FlowCatalogRoom[];
}

export const FLOW_CATALOG_GROUP_ORDER: CardDiscoveryGroup[] = [
	'devices',
	'time',
	'weather',
	'energy',
	'presence',
	'scenes',
	'notifications',
	'variables',
	'scripts',
	'flow-logic',
	'system',
];

export const FLOW_CATALOG_GROUP_LABELS: Record<CardDiscoveryGroup, string> = {
	devices: 'Devices',
	time: 'Time & schedule',
	weather: 'Weather',
	energy: 'Energy',
	presence: 'Presence',
	scenes: 'Scenes & alarms',
	notifications: 'Notifications',
	variables: 'Variables & virtual components',
	scripts: 'Scripts',
	'flow-logic': 'Flow logic',
	system: 'System',
};

const ROLE_DISCOVERY: Record<FlowCatalogRole, CardDiscoveryRole[]> = {
	when: ['trigger', 'timing'],
	and: ['condition', 'branch'],
	do: ['action', 'timing', 'state'],
};

export function nodeBelongsToCatalogRole(node: NodeTypeConfig, role: FlowCatalogRole): boolean {
	if (node.type === 'note') return false;
	if (!ROLE_DISCOVERY[role].includes(node.discovery.role)) return false;
	if (node.discovery.role === 'timing') {
		return role === 'when'
			? node.category === 'conditions'
			: role === 'do' && node.category !== 'conditions';
	}
	return true;
}

function genericSentence(node: NodeTypeConfig, role: FlowCatalogRole): string {
	const paletteKey = node.paletteKey ?? node.type;
	if (paletteKey === 'condition') return 'A device value changes…';
	if (paletteKey === 'deviceCheck') return 'Check a device value…';
	if (paletteKey === 'action') return 'Control a device…';
	const intent = node.discovery.intents[0] || node.description || node.label;
	if (role === 'when') return `When ${intent.replace(/^start |^react to /i, '').replace(/^a /, '')}`;
	if (role === 'and') return `And ${intent.replace(/^check |^make /i, '').replace(/^a /, '')}`;
	return intent.charAt(0).toUpperCase() + intent.slice(1);
}

function deviceInitialData(device: FlowCatalogDevice): Record<string, unknown> {
	return {
		deviceId: device.id,
		deviceName: device.name,
		channel: device.channel,
		device: { id: device.id, name: device.name, channel: device.channel },
	};
}

function humanize(value: unknown): string {
	return String(value ?? '')
		.replace(/^label_|^collapse_title_|^scene_label_|^prop_/, '')
		.replace(/[_-]+/g, ' ')
		.replace(/\b\w/g, (letter) => letter.toUpperCase())
		.trim();
}

function normalizedActionValue(action: any): unknown {
	if (Array.isArray(action.values) && action.values.length) return action.values[0];
	if (typeof action.min === 'number') return action.min;
	return true;
}

export function buildDeviceActionCards(
	device: FlowCatalogDevice,
	node: NodeTypeConfig,
	availability: FlowCatalogAvailability,
): FlowCatalogCard[] {
	const cards: FlowCatalogCard[] = [];
	for (const group of device.actionComponents ?? []) {
		for (const action of group?.actions ?? []) {
			const actionLabel = humanize(action.i18tr || action.i18nKey || action.title || action.id);
			const values =
				Array.isArray(action.values) && action.values.length ? action.values : [undefined];
			for (const value of values) {
				const valueLabel = value === undefined ? '[value]' : humanize(value);
				const title = value === undefined ? actionLabel : `${actionLabel}: ${valueLabel}`;
				cards.push({
					id: `${device.id}:action:${action.id}:${String(value ?? 'value')}`,
					nodeType: node.type,
					paletteKey: node.paletteKey ?? node.type,
					title,
					sentence: `${actionLabel} ${valueLabel}`,
					identity: device.name,
					icon: action.icon || node.icon,
					color: node.color,
					role: 'do',
					group: 'devices',
					groupLabel: FLOW_CATALOG_GROUP_LABELS.devices,
					breadcrumbs: [device.roomName, device.name],
					searchText:
						`${device.roomName} ${device.name} ${actionLabel} ${valueLabel}`.toLowerCase(),
					highlighted: Boolean(node.discovery.highlighted),
					premiumOnly: Boolean(node.premiumOnly),
					availability,
					initialData: {
						...deviceInitialData(device),
						actions: [
							{
								name: action.id,
								value: value ?? normalizedActionValue(action),
							},
						],
					},
				});
			}
		}
	}
	return cards;
}

function conditionOperator(value: unknown): {
	operator: string;
	cmpop: string;
} {
	const key = String(value ?? '')
		.toLowerCase()
		.replace(/\s+/g, '_');
	if (['less_than', 'below', 'lt'].includes(key)) return { operator: 'less_than', cmpop: 'lt' };
	if (['more_than', 'above', 'gt'].includes(key)) return { operator: 'more_than', cmpop: 'gt' };
	if (['not_equal', 'not', 'ne'].includes(key)) return { operator: 'not_equal', cmpop: 'ne' };
	return { operator: 'equal', cmpop: 'eq' };
}

export function buildDeviceConditionCards(
	device: FlowCatalogDevice,
	node: NodeTypeConfig,
	role: 'when' | 'and',
	availability: FlowCatalogAvailability,
): FlowCatalogCard[] {
	const cards: FlowCatalogCard[] = [];
	for (const component of device.conditionComponents ?? []) {
		const props = component?.props ?? {};
		if (!props.id || props.disabled) continue;
		const propertyLabel = humanize(props.name || props.title || props.i18tr || props.id);
		const isThreshold = props.input_value !== undefined && Array.isArray(props.values);
		const values =
			Array.isArray(props.key_values) && props.key_values.length
				? props.key_values.map((entry: any) => entry.key ?? entry.value ?? entry)
				: Array.isArray(props.values) && props.values.length
					? props.values
					: [undefined];
		for (const value of values) {
			const comparison = isThreshold
				? humanize(value)
				: value === undefined
					? 'changes'
					: humanize(value);
			const threshold = props.input_value ?? value ?? '';
			const operator = conditionOperator(isThreshold ? value : 'equal');
			const condition = {
				cond_name: props.id,
				add_info_ind: props.add_info_ind,
				input_index: props.input_index,
				operator: operator.operator,
				cmpop: operator.cmpop,
				value: threshold,
				cmpval: threshold,
			};
			const sentence = isThreshold
				? `${propertyLabel} is ${comparison} [value]`
				: `${propertyLabel} ${comparison}`;
			cards.push({
				id: `${device.id}:${role}:${props.id}:${String(value ?? 'value')}:${props.add_info_ind ?? props.input_index ?? ''}`,
				nodeType: node.type,
				paletteKey: node.paletteKey ?? node.type,
				title: sentence,
				sentence: role === 'when' ? `When ${sentence}` : `And ${sentence}`,
				identity: device.name,
				icon: props.icon || node.icon,
				color: node.color,
				role,
				group: 'devices',
				groupLabel: FLOW_CATALOG_GROUP_LABELS.devices,
				breadcrumbs: [device.roomName, device.name],
				searchText:
					`${device.roomName} ${device.name} ${propertyLabel} ${comparison}`.toLowerCase(),
				highlighted: Boolean(node.discovery.highlighted),
				premiumOnly: Boolean(node.premiumOnly),
				availability,
				initialData: {
					...deviceInitialData(device),
					_device_condition: condition,
					...condition,
				},
			});
		}
	}
	return cards;
}

export function buildFlowCatalogModel(args: {
	nodes: NodeTypeConfig[];
	role: FlowCatalogRole;
	devices: FlowCatalogDevice[];
	roomItems: Array<{ id: string; name: string }>;
	availabilityFor: (node: NodeTypeConfig, device?: FlowCatalogDevice) => FlowCatalogAvailability;
}): FlowCatalogModel {
	const roleNodes = args.nodes.filter((node) => nodeBelongsToCatalogRole(node, args.role));
	const cards = roleNodes.map((node): FlowCatalogCard => {
		const availability = args.availabilityFor(node);
		const groupLabel = FLOW_CATALOG_GROUP_LABELS[node.discovery.group];
		return {
			id: `general:${node.paletteKey ?? node.type}`,
			nodeType: node.type,
			paletteKey: node.paletteKey ?? node.type,
			title: node.label,
			sentence: genericSentence(node, args.role),
			identity: groupLabel,
			icon: node.icon,
			color: node.color,
			role: args.role,
			group: node.discovery.group,
			groupLabel,
			breadcrumbs: [groupLabel],
			searchText: [
				node.label,
				node.description,
				groupLabel,
				...node.discovery.intents,
				...node.discovery.searchAliases,
			]
				.join(' ')
				.toLowerCase(),
			highlighted: Boolean(node.discovery.highlighted),
			premiumOnly: Boolean(node.premiumOnly),
			availability,
		};
	});
	const criteria = FLOW_CATALOG_GROUP_ORDER.map((id) => ({
		id,
		label: FLOW_CATALOG_GROUP_LABELS[id],
		count: cards.filter((card) => card.group === id).length,
	})).filter((criterion) => criterion.count > 0);
	const roomIds = new Set(args.roomItems.map((room) => room.id));
	const rooms = [
		...args.roomItems.map((room) => ({
			...room,
			devices: args.devices.filter((device) => device.roomId === room.id),
		})),
		{
			id: '__other__',
			name: 'Other devices',
			devices: args.devices.filter((device) => !device.roomId || !roomIds.has(device.roomId)),
		},
	].filter((room) => room.devices.length > 0);
	return { cards, criteria, rooms };
}

export function searchFlowCatalog(cards: FlowCatalogCard[], query: string): FlowCatalogCard[] {
	const normalized = query.trim().toLowerCase();
	if (!normalized) return cards;
	return cards
		.map((card) => {
			const identity = `${card.identity} ${card.breadcrumbs.join(' ')}`.toLowerCase();
			const exactIdentity = identity.split(/\s+/).includes(normalized) || identity === normalized;
			const starts =
				card.title.toLowerCase().startsWith(normalized) || identity.startsWith(normalized);
			const matches = card.searchText.includes(normalized) || identity.includes(normalized);
			return {
				card,
				score: exactIdentity ? 0 : starts ? 1 : matches ? 2 : 99,
			};
		})
		.filter((entry) => entry.score < 99)
		.sort(
			(a, b) =>
				a.score - b.score ||
				Number(b.card.availability.available) - Number(a.card.availability.available) ||
				Number(b.card.highlighted) - Number(a.card.highlighted) ||
				a.card.title.localeCompare(b.card.title),
		)
		.map((entry) => entry.card);
}
